import React from 'react';
import { 
  XMarkIcon, 
  CheckCircleIcon,
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon, 
  ExclamationTriangleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

interface Insight {
  id: string;
  type: 'churn_risk' | 'expansion_opportunity' | 'attention_needed' | 'positive_trend';
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  customer_id: string;
  customer_name: string;
  created_at: string;
  status: 'active' | 'viewed' | 'dismissed' | 'resolved';
  source: string;
}

interface InsightDetailModalProps {
  insight: Insight | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate: (insightId: string, status: string) => void;
  isUpdating: boolean;
}

const InsightDetailModal: React.FC<InsightDetailModalProps> = ({
  insight,
  isOpen,
  onClose,
  onStatusUpdate,
  isUpdating
}) => {
  if (!isOpen || !insight) return null;

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'churn_risk':
        return <ArrowTrendingDownIcon className="h-6 w-6 text-destructive" />;
      case 'expansion_opportunity':
        return <ArrowTrendingUpIcon className="h-6 w-6 text-green-600 dark:text-green-400" />;
      case 'attention_needed':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />;
      case 'positive_trend':
        return <CheckCircleIcon className="h-6 w-6 text-primary" />;
      default:
        return <ChartBarIcon className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const colors = {
      high: 'bg-destructive/10 text-destructive border-destructive/20',
      medium: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
      low: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20'
    };
    return colors[severity as keyof typeof colors] || 'bg-muted text-muted-foreground border-border';
  };

  const getTypeBadge = (type: string) => {
    const colors = {
      churn_risk: 'bg-destructive/10 text-destructive border-destructive/20',
      expansion_opportunity: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
      attention_needed: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
      positive_trend: 'bg-primary/10 text-primary border-primary/20'
    };
    return colors[type as keyof typeof colors] || 'bg-muted text-muted-foreground border-border';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              {getInsightIcon(insight.type)}
              <div>
                <h2 className="text-xl font-semibold text-foreground">{insight.title}</h2>
                <div className="flex space-x-2 mt-1">
                  <Badge className={`${getTypeBadge(insight.type)} text-xs`}>
                    {insight.type.replace('_', ' ')}
                  </Badge>
                  <Badge className={`${getSeverityBadge(insight.severity)} text-xs`}>
                    {insight.severity}
                  </Badge>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Modal Content */}
          <div className="space-y-6">
            {/* Description */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Description</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {insight.description}
              </p>
            </div>

            {/* Customer Information */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Account</h3>
              <p className="text-sm text-muted-foreground">{insight.customer_name}</p>
            </div>

            {/* Timestamp and Source */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">Created</h3>
                <p className="text-sm text-muted-foreground">{formatDate(insight.created_at)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground mb-2">Source</h3>
                <p className="text-sm text-muted-foreground">{insight.source}</p>
              </div>
            </div>

            {/* Status */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">Status</h3>
              <Badge className={`${getSeverityBadge(insight.severity)} text-xs`}>
                {insight.status}
              </Badge>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => onStatusUpdate(insight.id, 'dismissed')}
              disabled={isUpdating}
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Dismiss
            </Button>
            <Button
              variant="outline"
              onClick={() => onStatusUpdate(insight.id, 'resolved')}
              disabled={isUpdating}
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Mark Resolved
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightDetailModal;
